import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Card, Button, Badge } from './ui';
import { safeErrorMessage } from '../utils/apiError';

function TeacherForm({ token }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [allocations, setAllocations] = useState([]);
  const [formData, setFormData] = useState({
    staff_id: '', full_name: '', national_id: '', phone: '', email: '',
    subjects: [], qualification: '', hire_date: '', status: 'active'
  });
  const [hasLogin, setHasLogin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingTeacher, setLoadingTeacher] = useState(isEdit);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetch('/api/v1/subjects', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setSubjectOptions(data.map((s) => s.name)))
      .catch(() => {});
  }, [token]);

  useEffect(() => {
    if (!isEdit) return;
    fetch(`/api/v1/subject-allocations?teacher_id=${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setAllocations)
      .catch(() => {});
  }, [id, isEdit, token]);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const response = await fetch(`/api/v1/teachers/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setFormData({
            staff_id: data.staff_id || '', full_name: data.full_name || '', national_id: data.national_id || '',
            phone: data.phone || '', email: data.email || '',
            subjects: Array.isArray(data.subjects) ? data.subjects : [],
            qualification: data.qualification || '', hire_date: data.hire_date ? data.hire_date.slice(0, 10) : '',
            status: data.status || 'active'
          });
          setHasLogin(Boolean(data.has_login));
        } else {
          setMessage({ type: 'error', text: 'Failed to load teacher' });
        }
      } catch {
        setMessage({ type: 'error', text: 'Network error' });
      } finally {
        setLoadingTeacher(false);
      }
    })();
  }, [id, isEdit, token]);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const toggleValue = (field, value) => {
    setFormData((prev) => {
      const current = prev[field];
      return {
        ...prev,
        [field]: current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    try {
      const response = await fetch(isEdit ? `/api/v1/teachers/${id}` : '/api/v1/teachers', {
        method: isEdit ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(formData)
      });
      if (response.ok) {
        setMessage({ type: 'success', text: isEdit ? 'Teacher updated successfully!' : 'Teacher created successfully!' });
        setTimeout(() => navigate('/teachers'), 1500);
      } else {
        setMessage({ type: 'error', text: await safeErrorMessage(response, `Failed to ${isEdit ? 'update' : 'create'} teacher`) });
      }
    } catch {
      setMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
    } finally {
      setLoading(false);
    }
  };

  if (loadingTeacher) return <Card>Loading teacher...</Card>;

  return (
    <Card>
      <h2 style={{ fontSize: '1.25rem', fontWeight: 700, marginBottom: '1.5rem' }}>
        {isEdit ? 'Edit Teacher' : 'Add New Teacher'}
      </h2>

      {message && <div className={`alert alert-${message.type}`}>{message.text}</div>}

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label>Staff ID *</label>
            <input type="text" name="staff_id" value={formData.staff_id} onChange={handleChange} required placeholder="e.g., T001" disabled={isEdit} />
          </div>
          <div className="form-group">
            <label>Full Name *</label>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} required />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>National ID</label>
            <input type="text" name="national_id" value={formData.national_id} onChange={handleChange} placeholder="00-000000A00" disabled={isEdit} />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder="+263712345678" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Email</label>
            <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="teacher@school.com" />
          </div>
          <div className="form-group">
            <label>Qualification</label>
            <input type="text" name="qualification" value={formData.qualification} onChange={handleChange} placeholder="e.g., BEd Mathematics" />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Subjects</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem 1rem', padding: '0.625rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
              {subjectOptions.length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>No subjects configured yet</span>}
              {subjectOptions.map((subject) => (
                <label key={subject} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontSize: '0.85rem', fontWeight: 400, cursor: 'pointer' }}>
                  <input type="checkbox" checked={formData.subjects.includes(subject)} onChange={() => toggleValue('subjects', subject)} />
                  {subject}
                </label>
              ))}
            </div>
          </div>
          {isEdit && (
            <div className="form-group">
              <label>Classes Assigned</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem', padding: '0.625rem 0.75rem', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)' }}>
                {allocations.length === 0 && <span style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>No subject allocations yet</span>}
                {allocations.map((a) => (
                  <Badge key={a.id} variant="info">{a.subject_name} · {a.class_display_name}</Badge>
                ))}
                <Link to={`/subject-allocations?teacher_id=${id}`} style={{ fontSize: '0.8rem' }}>
                  Manage allocations
                </Link>
              </div>
            </div>
          )}
        </div>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="teacher-hire-date">Hire Date</label>
            <input id="teacher-hire-date" type="date" name="hire_date" value={formData.hire_date} onChange={handleChange} />
          </div>
          {isEdit && (
            <div className="form-group">
              <label htmlFor="teacher-status">Status</label>
              <select id="teacher-status" name="status" value={formData.status} onChange={handleChange}>
                <option value="active">Active</option>
                <option value="on_leave">On leave</option>
                <option value="disabled">Disabled (cannot log in)</option>
              </select>
              <small>
                {formData.status === 'disabled'
                  ? (hasLogin ? 'Their login is switched off and any open session stops working. Their records are kept.' : 'They have no login yet; one created later stays switched off until this is changed.')
                  : formData.status === 'on_leave'
                    ? 'Shown as on leave in the directory. They can still sign in.'
                    : 'Working normally and able to sign in.'}
              </small>
            </div>
          )}
        </div>
        <Button type="submit" variant="primary" disabled={loading}>
          {loading ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Teacher')}
        </Button>
      </form>
    </Card>
  );
}

export default TeacherForm;
